import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { ION_TO_JOI } from '@buddy-technology/ion-helpers';
import RenderedField from './helpers/RenderedField';
import RenderedArray from './helpers/RenderedArray';
import IF from './helpers/IF';
import useSetDynamicValues from '../hooks/useSetDynamicValues';

/**
 * FieldPreviewer returns a react component displaying a field or group of fields.
 * @param {object} props
 * @param {object | Object[]} [props.defaultFieldValues] - an flat object of field/value pairs to initialize state
 * @param {object | Object[]} [props.fields] - a field object or array of field objects to preview
 * @param {string} [props.placesApiKey] - placesApiKey needed for the Address AutoComplete component
 * @param {boolean} [props.showValidateButton = false] - toggles whether to render validate button. Defaults to false.
 * @param {string} [props.validationMode = 'onTouched'] - the validation mode to use for fields component. Defaults to 'onTouched'
 * @returns React Component
 */
function FormPreviewComponent({
	defaultFieldValues,
	fields = [],
	ion,
	showValidateButton = false,
	placesApiKey,
	validationMode = 'onTouched',
}) {
	// Filter out reference fields so ITJ doesn't validate them.
	const schemaFields = fields.filter((input) => input.uiDisplay.element !== 'REFERENCE');
	const schema = ION_TO_JOI(schemaFields);

	const {
		register: rhfRegister,
		control,
		handleSubmit,
		getValues,
		getFieldState,
		setValue,
		trigger,
		clearErrors,
		watch,
		formState: {
			errors,
		},
	} = useForm({
		mode: validationMode,
		defaultValues: defaultFieldValues,
		resolver: joiResolver(
			schema.unknown(true),
			{
				abortEarly: false,
				errors: {
					wrap: {
						label: '',
						array: '"',
					},
				},
			},
		),
	});

	const updateAppState = useSetDynamicValues({
		getValues, getFieldState, setValue, flatApplication: fields,
	});

	// Pass this register function down instead of React Hook Form's register, that way we run our dynamic calcs when fields change.
	const register = (name, options = {}) => (
		rhfRegister(name, {
			...options,
			onChange: (e) => {
				if (options.onChange) {
					options.onChange(e);
				}
				updateAppState();
			},
		})
	);

	const sharedProps = {
		clearErrors,
		control,
		Controller,
		errors,
		flatApplication: fields,
		getFieldState,
		getValues,
		placesApiKey,
		register,
		setValue,
		trigger,
		updateAppState,
		variables: ion?.variables || {},
		watch,
	};

	// Need to run our appState calc on mount after initial values rendered to sync up state for derived values.
	useEffect(() => {
		updateAppState();
	}, []);

	return (
		<form onSubmit={handleSubmit(() => (true))} aria-label="form-preview-fields">
			{fields.map((fieldProps) => {
				const { id, type, uiDisplay } = fieldProps;
				if (type === 'ARRAY' && uiDisplay?.element !== 'CHECKBOX') {
					return (
						<RenderedArray
							{...fieldProps}
							{...sharedProps}
							key={id}
							viewId={`field-preview-array-field-${id}`}
						/>
					);
				}
				return (
					<RenderedField
						{...fieldProps}
						{...sharedProps}
						key={id}
						viewId={`field-preview-field-${id}`}
					/>
				);
			})}
			<IF condition={showValidateButton}>
				<div id="form-preview-validate-buttons" className="mt-4">
					<button
						type="submit"
						className="form-preview-validate-button-primary"
					>
						Validate
					</button>
					<button
						type="button"
						className="ml-2 form-preview-validate-button-secondary"
						onClick={() => {
							clearErrors();
						}}
					>
						Clear Errors
					</button>
				</div>
			</IF>
		</form>
	);
}

export default FormPreviewComponent;
